import React, { useState } from "react";
import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { Grip } from "../../Icons/icons";

// Draggable Component
const DraggableWord = ({ id, word, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: id.toString(),
  });

  // Apply transform for smooth dragging
  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : "none",
    opacity: isDragging ? 0.5 : 1, // Show opacity when dragging
  };

  return (
    <span
      ref={setNodeRef}
      style={style}
      className="p-2 rounded-md cursor-pointer flex items-center justify-between border-1 bg-blue-200 border-gray-400"
      onClick={() => onClick(word)} // Added onClick event
    >
      {/* Drag Grip */}
      <div
        className="h-8 w-4 flex items-center justify-center cursor-move pr-2"
        {...attributes}
        {...listeners}
      >
        <Grip />
      </div>

      {/* Word Content */}
      <span className="text-lg flex-1 text-center">{word}</span>
    </span>
  );
};

// Droppable Component
const DroppableWord = ({ id, sentence }) => {
  const { isOver, setNodeRef } = useDroppable({ id });

  const style = `p-2 rounded-md cursor-pointer inline-block border-2 border-dashed bg-white border-gray-300 ${
    isOver ? "bg-yellow-100 border-yellow-500" : ""
  }`;

  return (
    <span ref={setNodeRef} className={style}>
      {sentence || <span className="text-gray-400">___</span>}
    </span>
  );
};

// Main Component
const PreviewFillBlank = ({ data }) => {
  const [selectedWord, setSelectedWord] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);

  const handleDragEnd = (event) => {
    const { active, over } = event;
  
    // Ensure active.id is not null or undefined
    if (!active || !active.id || !over || over.id !== "blank") {
      return; // Do nothing if there's no valid active item or droppable target
    }
  
    // Safely parse the index from the active.id
    const draggedIndex = parseInt(active.id, 10); // Assuming the id is a string representation of the index
    const draggedWord = data.options[draggedIndex]; // Get the actual word using the index
  
    // Set the selected word
    setSelectedWord(draggedWord);
  
    // Check if the dragged word is the correct one
    setIsCorrect(draggedIndex === data.correctAnswer);
  };
  
  const handleOptionClick = (word) => {
    setSelectedWord(word); // Set the selected word on click

    // Check if the clicked word is correct
    const isCorrectWord = word === data.options[data.correctAnswer];
    setIsCorrect(isCorrectWord);
  };

  const formattedQuestion = data.question.replace(/\.{2,}/g, "___");

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div className="p-6 bg-white shadow-md rounded-lg w-full max-w-4xl mx-auto mt-10">
        {/* Question Section */}
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-6 flex flex-wrap items-center justify-center gap-4">
          {formattedQuestion.split("___").map((part, index, arr) =>
            index < arr.length - 1 ? (
              <React.Fragment key={index}>
                {part} <DroppableWord id="blank" sentence={selectedWord} />
              </React.Fragment>
            ) : (
              part
            )
          )}
        </h2>

        {/* Instruction */}
        <p className="text-gray-500 font-medium text-center mb-4">
          Drag or click the correct answer into the blank.
        </p>

        {/* Options */}
        <div className="flex justify-center">
          <div className="grid grid-cols-2 gap-4 w-60">
            {data.options.map((word, index) => (
              <DraggableWord
                key={index}
                id={index.toString()}
                word={word}
                onClick={handleOptionClick} // Added onClick handler for clicking options
              />
            ))}
          </div>
        </div>

        {/* Feedback Message */}
        {isCorrect !== null && (
          <div
            className={`mt-6 py-3 px-4 rounded-lg text-center font-bold ${
              isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}
          >
            {isCorrect ? "✅ Correct! Well done!" : "❌ Incorrect. Try again!"}
          </div>
        )}
      </div>
    </DndContext>
  );
};

export default PreviewFillBlank;

